/**
 * MwCalibrationDriftPanel
 *
 * Read-only analytics: calibration drift per Match Winner.
 * Renders summary + EV/odds buckets + worst-drift patterns + warnings.
 *
 * Pure analytics — no Telegram, Policy Guard, Kelly, or pick changes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { adminGet } from '../data/api';
import { RefreshCcw, Crosshair, AlertTriangle } from 'lucide-react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

const fmtPct = (v, d = 1) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(d)}%`;
};
const fmtPctSigned = (v, d = 1) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(d)}%`;
};
const fmtSignedPp = (v, d = 1) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(d)}pp`;
};
const fmtNum = (v, d = 2) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(d);
};
const fmtEv = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
};

const driftCls = (v) =>
  v == null ? 'text-zinc-500'
  : v >= -2 && v <= 2 ? 'text-emerald-300'
  : v >= -10 ? 'text-amber-300'
  : 'text-rose-300';

const evCls = (v) =>
  v == null ? 'text-zinc-500'
  : v >= 5 ? 'text-emerald-300'
  : v >= 0 ? 'text-zinc-300'
  : 'text-rose-300';

const roiCls = (v) =>
  v == null ? 'text-zinc-500'
  : v > 0.05 ? 'text-emerald-300'
  : v > 0 ? 'text-emerald-400'
  : v >= -0.05 ? 'text-zinc-400'
  : 'text-rose-300';

const WARNING_LABEL = {
  LOW_SAMPLE: 'Sample troppo piccolo (<100 settled)',
  EV_CALIBRATION_RISK: 'EV positivo ma ROI negativo — calibrazione MW da rivedere',
  SEVERE_NEGATIVE_DRIFT: 'Drift severamente negativo (≤ -10pp)',
  HIGH_EV_NEGATIVE_ROI: 'EV ≥ +10% ma ROI realizzato negativo',
};

export default function MwCalibrationDriftPanel({ pin }) {
  const [days, setDays] = useState(30);
  const [profileType, setProfileType] = useState('');
  const [guard, setGuard] = useState('');
  const [mwNoneLeakOnly, setMwNoneLeakOnly] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (profileType) params.set('profile_type', profileType);
      if (guard) params.set('strongest_guard', guard);
      if (mwNoneLeakOnly !== 'all') params.set('mw_none_leak_guard', mwNoneLeakOnly);
      const r = await adminGet(`/tt/admin/mw-calibration-drift?${params}`, pin);
      setData(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [pin, days, profileType, guard, mwNoneLeakOnly]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const ev = data?.ev_buckets || [];
  const odds = data?.odds_buckets || [];
  const probs = data?.probability_buckets || [];
  const byGuard = data?.by_guard || [];
  const byProfile = data?.by_profile || [];
  const worst = data?.worst_drift_patterns || [];
  const best = data?.best_calibrated_patterns || [];
  const warnings = data?.warnings || [];

  return (
    <div
      data-testid="mw-calibration-drift-panel"
      className="rounded-sm p-4 space-y-3"
      style={{ background: '#18181B', border: '1px solid #27272A', ...mono }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crosshair size={14} className="text-cyan-400" />
          <span className="text-zinc-100 text-sm font-semibold tracking-wide">
            MW Calibration Drift
          </span>
          <span className="text-[9px] text-zinc-500">
            audit · settled MW alerts only · EV vs realized ROI
          </span>
        </div>
        <button
          data-testid="mwcd-reload-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50 text-[10px]"
        >
          <RefreshCcw size={10} className={loading ? 'animate-spin' : ''} />
          {loading ? 'loading' : 'reload'}
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]">
        <select
          data-testid="mwcd-days"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value={7}>7d</option>
          <option value={14}>14d</option>
          <option value={30}>30d</option>
          <option value={90}>90d</option>
        </select>
        <select
          data-testid="mwcd-profile"
          value={profileType}
          onChange={(e) => setProfileType(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="">Tutti i profili</option>
          <option value="STOMP_CLEAN">STOMP_CLEAN</option>
          <option value="CONTROLLED_FAST">CONTROLLED_FAST</option>
          <option value="CONTROLLED_LONG">CONTROLLED_LONG</option>
          <option value="BALANCED_LONG">BALANCED_LONG</option>
          <option value="TRUE_CHAOS">TRUE_CHAOS</option>
          <option value="BALANCED">BALANCED</option>
        </select>
        <select
          data-testid="mwcd-guard"
          value={guard}
          onChange={(e) => setGuard(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="">Tutti i guard</option>
          <option value="no_guard">no_guard</option>
          <option value="MW_NONE_LEAK_GUARD">MW_NONE_LEAK_GUARD</option>
          <option value="MW_FRAGILE_SWITCH_TO_OU">MW_FRAGILE</option>
          <option value="FAST_MATCH_RISK_STRONG">FAST_STRONG</option>
          <option value="OVERCONFIDENT_SETSCORE">OVERCONFIDENT</option>
        </select>
        <select
          data-testid="mwcd-mwnone"
          value={mwNoneLeakOnly}
          onChange={(e) => setMwNoneLeakOnly(e.target.value)}
          className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200"
        >
          <option value="all">MW none leak: tutti</option>
          <option value="true">solo flagged</option>
          <option value="false">solo NON flagged</option>
        </select>
      </div>

      {err && (
        <div className="text-[10px] text-rose-300 bg-rose-950/40 border border-rose-800 rounded-sm px-2 py-1">
          {err}
        </div>
      )}

      {warnings.length > 0 && (
        <div
          data-testid="mwcd-warnings"
          className="flex items-start gap-2 text-[10px] text-amber-200 bg-amber-950/40 border border-amber-800 rounded-sm px-2 py-1"
        >
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            {warnings.map((w) => (
              <div key={w}>{WARNING_LABEL[w] || w}</div>
            ))}
          </div>
        </div>
      )}

      {/* Summary band */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
        <Cell label="analyzed" value={summary.analyzed ?? 0} testid="mwcd-analyzed" />
        <Cell label="settled" value={summary.settled ?? 0} testid="mwcd-settled" />
        <Cell label="win rate" value={fmtPct(summary.win_rate)}
          tone={summary.win_rate >= 0.5 ? 'pos' : 'neg'}
          testid="mwcd-winrate" />
        <Cell label="avg model prob"
          value={summary.avg_model_prob != null ? `${(summary.avg_model_prob * 100).toFixed(1)}%` : '—'}
          testid="mwcd-prob" />
        <Cell label="avg odds" value={fmtNum(summary.avg_odds, 2)}
          testid="mwcd-odds" />
        <Cell label="avg EV" value={fmtEv(summary.avg_ev)}
          tone={summary.avg_ev > 0 ? 'pos' : 'neg'} testid="mwcd-avgev" />
        <Cell label="realized ROI"
          value={fmtPctSigned(summary.realized_roi, 1)}
          tone={summary.realized_roi > 0 ? 'pos' : 'neg'}
          testid="mwcd-roi" />
        <Cell label="drift"
          value={fmtSignedPp(summary.calibration_drift_pp)}
          tone={summary.calibration_drift_pp >= -2 ? 'pos'
                : summary.calibration_drift_pp >= -10 ? 'warn' : 'neg'}
          testid="mwcd-drift" />
        <Cell label="brier" value={fmtNum(summary.brier, 4)}
          testid="mwcd-brier" />
        <Cell label="logloss" value={fmtNum(summary.logloss, 4)}
          testid="mwcd-logloss" />
        <Cell label="PnL 1u"
          value={summary.pnl_flat_1u != null
            ? `${summary.pnl_flat_1u >= 0 ? '+' : ''}${summary.pnl_flat_1u.toFixed(2)}`
            : '—'}
          tone={summary.pnl_flat_1u > 0 ? 'pos' : 'neg'}
          testid="mwcd-pnl" />
        <Cell label="W/L"
          value={`${summary.wins ?? 0}/${summary.losses ?? 0}`}
          testid="mwcd-wl" />
      </div>

      {/* EV buckets */}
      <BucketTable
        title="EV BUCKETS · drift = realized_roi - avg_ev"
        rows={ev}
        bucketKey="bucket"
        testid="mwcd-ev-buckets"
      />

      {/* Odds buckets */}
      <BucketTable
        title="ODDS BUCKETS"
        rows={odds}
        bucketKey="bucket"
        testid="mwcd-odds-buckets"
      />

      {/* Probability buckets */}
      <BucketTable
        title="PROBABILITY BUCKETS"
        rows={probs}
        bucketKey="bucket"
        testid="mwcd-prob-buckets"
      />

      {/* By guard */}
      {byGuard.length > 0 && (
        <BucketTable
          title="BY GUARD"
          rows={byGuard}
          bucketKey="guard"
          testid="mwcd-by-guard"
        />
      )}

      {/* By profile */}
      {byProfile.length > 0 && (
        <BucketTable
          title="BY SETPROFILE"
          rows={byProfile}
          bucketKey="profile_type"
          testid="mwcd-by-profile"
        />
      )}

      {/* Worst-drift / best-calibrated patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PatternsTable
          title="WORST DRIFT · EV>0 & ROI<0 · settled≥5"
          rows={worst}
          testid="mwcd-worst"
        />
        <PatternsTable
          title="BEST CALIBRATED · |drift| smallest"
          rows={best}
          testid="mwcd-best"
        />
      </div>

      <div className="text-[9px] text-zinc-600">
        window {data?.days || days}d · MW market only · drift = realized_roi -
        avg_ev (positive = under-estimated, negative = leak) · audit-only
      </div>
    </div>
  );
}

function Cell({ label, value, tone, testid }) {
  const cls = tone === 'pos' ? 'text-emerald-300'
    : tone === 'neg' ? 'text-rose-300'
    : tone === 'warn' ? 'text-amber-300'
    : 'text-zinc-100';
  return (
    <div data-testid={testid}
         className="rounded-sm bg-zinc-950/60 border border-zinc-800 p-1.5">
      <div className="text-[9px] text-zinc-500 tracking-wider">{label}</div>
      <div className={`${cls} font-semibold`}>{value}</div>
    </div>
  );
}

function BucketTable({ title, rows, bucketKey, testid }) {
  if (!rows || rows.length === 0) return null;
  const visible = rows.filter((r) => (r.n || 0) > 0);
  if (visible.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="text-[10px] text-zinc-500 tracking-wider">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]" data-testid={testid}>
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="text-left py-1">{bucketKey}</th>
              <th className="text-right">n</th>
              <th className="text-right">settled</th>
              <th className="text-right">W%</th>
              <th className="text-right">avg p</th>
              <th className="text-right">avg odds</th>
              <th className="text-right">avg EV</th>
              <th className="text-right">ROI</th>
              <th className="text-right">drift</th>
              <th className="text-right">PnL</th>
              <th className="text-left">!</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r[bucketKey]}
                  data-testid={`${testid}-row-${r[bucketKey]}`}
                  className="text-zinc-300 border-b border-zinc-900/50">
                <td className="py-1 text-zinc-400">{r[bucketKey]}</td>
                <td className="text-right">{r.n}</td>
                <td className="text-right text-zinc-500">{r.settled}</td>
                <td className="text-right">{fmtPct(r.win_rate)}</td>
                <td className="text-right text-zinc-500">
                  {r.avg_model_prob != null ? `${(r.avg_model_prob * 100).toFixed(0)}%` : '—'}
                </td>
                <td className="text-right text-zinc-500">{fmtNum(r.avg_odds, 2)}</td>
                <td className={`text-right ${evCls(r.avg_ev)}`}>{fmtEv(r.avg_ev)}</td>
                <td className={`text-right ${roiCls(r.realized_roi)}`}>
                  {fmtPctSigned(r.realized_roi, 1)}
                </td>
                <td className={`text-right font-semibold ${driftCls(r.drift_pp)}`}>
                  {fmtSignedPp(r.drift_pp)}
                </td>
                <td className={`text-right ${roiCls(r.realized_roi)}`}>
                  {r.pnl_flat_1u != null
                    ? `${r.pnl_flat_1u >= 0 ? '+' : ''}${r.pnl_flat_1u.toFixed(2)}`
                    : '—'}
                </td>
                <td className="text-amber-300 text-[9px]">{r.warning ? '⚠' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PatternsTable({ title, rows, testid }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] text-zinc-500 tracking-wider">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]" data-testid={testid}>
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="text-left py-1">pattern</th>
              <th className="text-right">n</th>
              <th className="text-right">avg EV</th>
              <th className="text-right">ROI</th>
              <th className="text-right">drift</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-2">—</td></tr>
            )}
            {(rows || []).map((p, i) => (
              <tr key={p.pattern_key}
                  data-testid={`${testid}-row-${i}`}
                  className="text-zinc-300 border-b border-zinc-900/50">
                <td className="py-1 truncate max-w-[16rem]" title={p.pattern_key}>
                  {p.pattern_key}
                </td>
                <td className="text-right">{p.n}</td>
                <td className={`text-right ${evCls(p.avg_ev)}`}>{fmtEv(p.avg_ev)}</td>
                <td className={`text-right ${roiCls(p.realized_roi)}`}>
                  {fmtPctSigned(p.realized_roi, 1)}
                </td>
                <td className={`text-right font-semibold ${driftCls(p.drift_pp)}`}>
                  {fmtSignedPp(p.drift_pp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
